import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

const NAV = [
  { label: "Home", to: "/", icon: "🏠" },
  { label: "Study Materials", to: "/study", icon: "📚" },
  { label: "Paintings & Me", to: "/paintings", icon: "🎨" },
  { label: "Meow", to: "/thecat", icon: "🐱" },
  { label: "About Me", to: "/aboutme", icon: "👤" },
];

export default function Sidebar() {
  const [open, setOpen] = useState(false);
  const { dark, toggle } = useTheme();
  const location = useLocation();

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <>
      {/* Hamburger — always visible */}
      <button
        aria-label="Open navigation"
        onClick={() => setOpen(true)}
        className="fixed top-4 left-4 z-50 p-2.5 rounded-xl shadow-md backdrop-blur-sm transition-all hover:shadow-lg
          bg-white/80 dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700"
      >
        <span className="block w-5 h-0.5 bg-gray-700 dark:bg-gray-200 mb-1.5 transition-colors" />
        <span className="block w-5 h-0.5 bg-gray-700 dark:bg-gray-200 mb-1.5 transition-colors" />
        <span className="block w-5 h-0.5 bg-gray-700 dark:bg-gray-200 transition-colors" />
      </button>

      {/* Backdrop */}
      <div
        onClick={() => setOpen(false)}
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px] transition-opacity duration-300
          ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      />

      {/* Sidebar panel */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 z-50 flex flex-col shadow-2xl
          transition-transform duration-300 ease-in-out
          bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800
          ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <span className="font-bold text-lg text-gray-900 dark:text-white tracking-tight">
            5MNS ✦
          </span>
          <button
            aria-label="Close navigation"
            onClick={() => setOpen(false)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 dark:hover:text-white
              hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map(({ label, to, icon }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 mx-3 px-4 py-3 rounded-xl mb-1
                transition-all duration-200 text-sm font-medium
                ${isActive(to)
                  ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                  : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
            >
              <span className="text-xl leading-none">{icon}</span>
              <span>{label}</span>
              {isActive(to) && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-500" />
              )}
            </Link>
          ))}
        </nav>

        {/* Dark mode toggle */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-800">
          <button
            aria-label="Toggle dark mode"
            onClick={toggle}
            className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border
              text-sm font-medium transition-all duration-200
              ${dark
                ? "border-gray-700 hover:bg-gray-800 text-gray-300"
                : "border-gray-200 hover:bg-gray-50 text-gray-600"
              }`}
          >
            <span>{dark ? "☀️ Light Mode" : "🌙 Dark Mode"}</span>
            <span
              className={`relative w-10 h-5 rounded-full transition-colors duration-300
                ${dark ? "bg-emerald-500" : "bg-gray-300"}`}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow
                  transition-transform duration-300
                  ${dark ? "translate-x-5" : "translate-x-0.5"}`}
              />
            </span>
          </button>
        </div>
      </aside>
    </>
  );
}
